// src/components/Transactions/TransactionDetailsDialog.tsx
import React from "react";
import {
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Divider,
  Stack,
  TextField,
  Box,
} from "@mui/material";
import dayjs from "dayjs";

import type { Transaction } from "../../pages/TransactionsPage";

type TransactionDetailsDialogProps = {
  open: boolean;
  onClose: () => void;
  transaction: Transaction | null;
  onDelete?: (id: string) => Promise<void>;
  onSave?: (id: string, patch: { notes?: string | null; merchant?: string | null; description?: string }) => Promise<void>;
};

export default function TransactionDetailsDialog({
  open,
  onClose,
  transaction,
  onDelete,
  onSave,
}: TransactionDetailsDialogProps) {
  const [confirmingDelete, setConfirmingDelete] = React.useState(false);
  const [deleteError, setDeleteError] = React.useState<string | null>(null);
  const [deleting, setDeleting] = React.useState(false);

  // Editable fields
  const [notes, setNotes] = React.useState('');
  const [merchant, setMerchant] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);

  // Reset form when transaction changes / dialog opens
  React.useEffect(() => {
    if (open && transaction) {
      setNotes(transaction.notes ?? '');
      setMerchant(transaction.merchant ?? '');
      setSaveError(null);
    }
    if (!open) {
      setConfirmingDelete(false);
      setDeleteError(null);
      setDeleting(false);
      setSaving(false);
      setSaveError(null);
    }
  }, [open, transaction]);

  if (!transaction) return null;

  const isDirty =
    (notes !== (transaction.notes ?? '')) ||
    (merchant !== (transaction.merchant ?? ''));

  const handleSave = async () => {
    if (!onSave || !isDirty) return;
    setSaving(true);
    setSaveError(null);
    try {
      await onSave(transaction.id, {
        notes: notes || null,
        merchant: merchant || null,
      });
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Transaction Details</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {/* --- Basic Info --- */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Date
            </Typography>
            <Typography variant="body1">
              {dayjs(transaction.date).format("MMMM D, YYYY")}
            </Typography>
          </Box>

          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Description
            </Typography>
            <Typography variant="body1">{transaction.description}</Typography>
          </Box>

          <Stack direction="row" spacing={2}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Category
              </Typography>
              <Typography variant="body1">{transaction.category}</Typography>
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Account
              </Typography>
              <Typography variant="body1">{transaction.account}</Typography>
            </Box>
          </Stack>

          <Divider />

          {/* --- Financial Info --- */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Amount
            </Typography>
            <Typography
              variant="h6"
              color={transaction.amount < 0 ? "error.main" : "success.main"}
            >
              {transaction.amount < 0 ? "-" : "+"}${Math.abs(transaction.amount).toFixed(2)}{" "}
              {transaction.currency}
            </Typography>
          </Box>

          {/* --- Extended Info --- */}
          <TextField
            label="Merchant"
            placeholder="e.g. Walmart, Amazon"
            fullWidth
            value={merchant}
            onChange={(e) => setMerchant(e.target.value)}
          />

          <TextField
            label="Notes / Comments"
            placeholder="Add any notes or context..."
            fullWidth
            multiline
            minRows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />

          {saveError && (
            <Alert severity="error">{saveError}</Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ justifyContent: "space-between" }}>
        {confirmingDelete ? (
          <>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" color="error">
                Permanently delete this transaction?
              </Typography>
              {deleteError && (
                <Alert severity="error" sx={{ mt: 0.5, py: 0 }}>
                  {deleteError}
                </Alert>
              )}
            </Box>
            <Box sx={{ display: "flex", gap: 1, ml: 1, flexShrink: 0 }}>
              <Button
                onClick={() => { setConfirmingDelete(false); setDeleteError(null); }}
                disabled={deleting}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                disabled={deleting}
                onClick={async () => {
                  setDeleting(true);
                  setDeleteError(null);
                  try {
                    await onDelete?.(transaction.id);
                  } catch (err) {
                    setDeleteError(err instanceof Error ? err.message : 'Delete failed');
                    setDeleting(false);
                  }
                }}
              >
                {deleting ? <CircularProgress size={16} color="inherit" /> : 'Delete'}
              </Button>
            </Box>
          </>
        ) : (
          <>
            {onDelete ? (
              <Button color="error" onClick={() => setConfirmingDelete(true)}>
                Delete
              </Button>
            ) : (
              <Box />
            )}
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button onClick={onClose}>Close</Button>
              <Button
                variant="contained"
                color="primary"
                disabled={!isDirty || saving}
                onClick={handleSave}
              >
                {saving ? <CircularProgress size={16} color="inherit" /> : 'Save Changes'}
              </Button>
            </Box>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
